'use client';

import { SliceZone } from '@prismicio/react';
import { usePathname } from 'next/navigation';
import { PrismicNextLink } from '@prismicio/next';
import { Fragment, useEffect, useState } from 'react';
import { faBars } from '@fortawesome/pro-regular-svg-icons';

// Contexts
import { useSiteNavigationContext } from '@/context/SiteNavigationProvider';

// Components
import { Box, List, Button, Drawer, Heading } from '@preventx/oxygen';

// Slices
import { components as navigationComponents } from '@/slices/navigation';
import { SectionIdentifierNavigationItem } from './SectionIdentifierNavigationItem';

type DrawerSiteNavigationProps = {
  openMenuLabel: string;
  navigationTitle: string;
};

export const DrawerSiteNavigation = ({
  openMenuLabel,
  navigationTitle,
}: DrawerSiteNavigationProps) => {
  const pathName = usePathname();

  const { navigation } = useSiteNavigationContext();

  const [isNavigationOpen, setIsNavigationOpen] = useState<boolean>(false);

  useEffect(() => {
    setIsNavigationOpen(false);
  }, [pathName]);

  if (navigation.length === 0) {
    return;
  }

  return (
    <Box component="div">
      <Box component="div" className="md:hidden">
        <Button
          type="button"
          id="drawer-button"
          variant="icon-button"
          icon={{ definition: faBars }}
          onClick={() => setIsNavigationOpen(true)}
        >
          {openMenuLabel}
        </Button>
      </Box>

      <Drawer
        position="left"
        title={navigationTitle}
        open={isNavigationOpen}
        setOpen={setIsNavigationOpen}
      >
        {navigation.map(
          ({ id, link, section, dropdown, multiColumnDropdown }) => (
            <Fragment key={`drawer-site-navigation-${id}`}>
              {link && (
                <List
                  ul="stack-nav"
                  options={[
                    {
                      key: `drawer-site-navigation-link-${id}`,
                      content: <PrismicNextLink field={link} />,
                    },
                  ]}
                />
              )}

              {section && (
                <List
                  ul="stack-nav"
                  options={[
                    {
                      key: `drawer-site-navigation-link-${id}`,
                      content: (
                        <SectionIdentifierNavigationItem
                          id={section.id}
                          title={section.title}
                          label={section.label}
                        />
                      ),
                    },
                  ]}
                />
              )}

              {dropdown && (
                <>
                  <Heading component="h3" className="stack-nav__heading">
                    {dropdown.label}
                  </Heading>

                  <List
                    ul="stack-nav"
                    options={dropdown.items.map(({ id, link }) => ({
                      key: `drawer-site-navigation-listitem-${id}`,
                      content: <PrismicNextLink field={link} />,
                    }))}
                  />
                </>
              )}

              {multiColumnDropdown && (
                <>
                  <Heading component="h3" className="stack-nav__heading">
                    {multiColumnDropdown.label}
                  </Heading>

                  {multiColumnDropdown.columnOne && (
                    <Box component="div">
                      <SliceZone
                        components={navigationComponents}
                        slices={multiColumnDropdown.columnOne}
                      />
                    </Box>
                  )}

                  {multiColumnDropdown.columnTwo && (
                    <Box component="div">
                      <SliceZone
                        components={navigationComponents}
                        slices={multiColumnDropdown.columnTwo}
                      />
                    </Box>
                  )}

                  {multiColumnDropdown.columnThree && (
                    <Box component="div">
                      <SliceZone
                        components={navigationComponents}
                        slices={multiColumnDropdown.columnThree}
                      />
                    </Box>
                  )}
                </>
              )}
            </Fragment>
          ),
        )}
      </Drawer>
    </Box>
  );
};
